import type { ElementType, MouseEvent } from 'react';
import {
  Breadcrumb as AntBreadcrumb,
  BreadcrumbProps as AntBreadcrumbProps,
  Typography,
  theme,
} from 'antd';
import { HomeOutlined } from '@ant-design/icons';
import styled from 'styled-components';
import type { GlobalToken } from 'antd';
import { typography } from '../../theme/typography';

export interface BreadcrumbItem {
  /** Label text */
  label: string;
  /** Link URL (makes item clickable) */
  href?: string;
  /** Click handler */
  onClick?: () => void;
  /**
   * Element or component the link renders as. Defaults to `'a'`. Pass a
   * router `Link` with `to` (e.g. `{ as: Link, to: '/path' }`) for client-side
   * navigation; the router then handles plain vs. modified (new tab) clicks.
   * The lib stays router-agnostic — the consumer supplies the link component.
   */
  as?: ElementType;
  /** Target for a router link passed in `as`. */
  to?: string;
  /** Icon to show before label */
  icon?: React.ReactNode;
}

export interface BreadcrumbProps extends Omit<AntBreadcrumbProps, 'items'> {
  /** Breadcrumb items */
  items: BreadcrumbItem[];
  /** Custom separator */
  separator?: React.ReactNode;
  /** Whether to show home icon for first item */
  showHomeIcon?: boolean;
  /** Maximum items to show before collapsing (0 = no collapse) */
  maxItems?: number;
  /**
   * Text size. 'sm' (default, fontSizeSM) for compact spots; 'md' (fontSize)
   * above a page title or as the title of a compact header.
   */
  size?: BreadcrumbSize;
  /**
   * Keep the trail on one line and truncate the current (last) item with an
   * ellipsis when it doesn't fit. The full label shows in a tooltip, only
   * when it is truncated.
   */
  ellipsis?: boolean;
}

export type BreadcrumbSize = 'sm' | 'md';

const sizeFontSize = (token: GlobalToken, size: BreadcrumbSize): number =>
  size === 'md' ? token.fontSize : token.fontSizeSM;

interface StyledBreadcrumbProps {
  $token: GlobalToken;
  $size: BreadcrumbSize;
  $ellipsis: boolean;
}

const ellipsisStyles = `
  ol {
    flex-wrap: nowrap;
  }

  li {
    flex-shrink: 0;
    white-space: nowrap;
  }

  li:last-child {
    flex-shrink: 1;
    min-width: 0;

    .ant-breadcrumb-link {
      display: block;
    }

    /* Typography.Text truncates; it takes the trail's text style. */
    .ant-typography {
      color: inherit;
      font-size: inherit;
    }
  }
`;

const StyledBreadcrumb = styled(AntBreadcrumb)<StyledBreadcrumbProps>`
  /* Breadcrumbs are always Inter, whatever the surrounding font. */
  font-family: ${typography.fontFamily.secondary};
  font-size: ${({ $token, $size }) => sizeFontSize($token, $size)}px;

  /* Explicit so a bold parent (e.g. a page title slot) doesn't leak in. */
  font-weight: ${typography.fontWeight.regular};

  ol {
    align-items: center;
  }

  /* Every item shares the trail's size and weight; links differ by look only:
     the design system's link style (primary color, underlined). */
  .ant-breadcrumb-link {
    color: ${({ $token }) => $token.colorText};
  }

  /* Doubled class beats Ant Design's own link styles (hover background,
     padding, negative margin). */
  && .ant-breadcrumb-link a {
    height: auto;
    margin: 0;
    padding: 0;
    border-radius: ${({ $token }) => $token.borderRadiusSM}px;
    background: transparent;
    color: ${({ $token }) => $token.colorPrimary};
    text-decoration: underline;
    text-underline-offset: ${({ $token }) => $token.lineWidthBold}px;
    transition: color ${({ $token }) => $token.motionDurationMid} ease;

    &:hover {
      background: transparent;
      color: ${({ $token }) => $token.colorPrimaryHover};
    }

    &:focus-visible {
      outline: ${({ $token }) =>
        `${$token.lineWidthBold}px solid ${$token.colorPrimary}`};
      outline-offset: ${({ $token }) => $token.lineWidthBold}px;
    }
  }

  /* Centered so an icon separator (e.g. a chevron) lines up with the text,
     and sized from the theme so callers pass the icon without a size. */
  .ant-breadcrumb-separator {
    display: inline-flex;
    align-items: center;
    color: ${({ $token }) => $token.colorTextDisabled};
    margin: 0 ${({ $token }) => $token.marginXS}px;

    svg {
      width: ${({ $token }) => $token.fontSizeSM}px;
      height: ${({ $token }) => $token.fontSizeSM}px;
    }
  }

  ${({ $ellipsis }) => ($ellipsis ? ellipsisStyles : '')}
`;

interface BreadcrumbLinkProps {
  $token: GlobalToken;
  $clickable: boolean;
}

const BreadcrumbLink = styled.a<BreadcrumbLinkProps>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS + 2}px;
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};

  .anticon {
    font-size: ${({ $token }) => $token.fontSize}px;
  }
`;

const BreadcrumbText = styled.span<{ $token: GlobalToken }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ $token }) => $token.marginXXS + 2}px;

  .anticon {
    font-size: ${({ $token }) => $token.fontSize}px;
  }
`;

/**
 * Breadcrumb component based on Libertify Design System
 *
 * Now uses Ant Design theme tokens for consistent styling.
 *
 * @example
 * ```tsx
 * import { Breadcrumb } from '@libertify/ui';
 *
 * const items = [
 *   { label: 'Home', href: '/' },
 *   { label: 'Documents', href: '/documents' },
 *   { label: 'Q4 Report' },
 * ];
 *
 * <Breadcrumb items={items} />
 * <Breadcrumb items={items} showHomeIcon />
 * ```
 */
export const Breadcrumb = ({
  items,
  separator = '/',
  showHomeIcon = false,
  maxItems = 0,
  size = 'sm',
  ellipsis = false,
  ...props
}: BreadcrumbProps) => {
  const { token } = theme.useToken();

  // Handle collapse if maxItems is set
  let displayItems = [...items];

  if (maxItems > 0 && items.length > maxItems) {
    const firstItems = items.slice(0, 1);
    const lastItems = items.slice(-(maxItems - 2));
    displayItems = [
      ...firstItems,
      { label: '...', href: undefined, onClick: undefined },
      ...lastItems,
    ];
  }

  // Convert to Ant Design format
  const antItems = displayItems.map((item, index) => {
    const isFirst = index === 0;
    const isLast = index === displayItems.length - 1;
    const isClickable = !!(item.href || item.to || item.onClick) && !isLast;
    const icon = isFirst && showHomeIcon ? <HomeOutlined /> : item.icon;

    const content = (
      <>
        {icon}
        {item.label}
      </>
    );

    if (isClickable) {
      return {
        title: (
          <BreadcrumbLink
            as={item.as}
            // Forwarded to the `as` link component (e.g. a router Link).
            {...(item.to === undefined ? {} : { to: item.to })}
            $token={token}
            $clickable={isClickable}
            href={item.href}
            onClick={(e: MouseEvent) => {
              if (item.onClick) {
                e.preventDefault();
                item.onClick();
              }
            }}
          >
            {content}
          </BreadcrumbLink>
        ),
      };
    }

    // The current item truncates; the tooltip shows only when it does.
    if (ellipsis && isLast) {
      return {
        title: (
          <Typography.Text ellipsis={{ tooltip: item.label }}>
            {icon}
            {item.label}
          </Typography.Text>
        ),
      };
    }

    return {
      title: <BreadcrumbText $token={token}>{content}</BreadcrumbText>,
    };
  });

  return (
    <StyledBreadcrumb
      $token={token}
      $size={size}
      $ellipsis={ellipsis}
      items={antItems}
      separator={separator}
      {...props}
    />
  );
};

export default Breadcrumb;
